<script setup>
import { computed, inject, ref } from 'vue'
import { useMainStore } from '@/stores/main'
import * as Alerts from '@/services/Alerts'
import * as Server from '@/services/Server'
import EntryForm from './EntryForm.vue'

const tournament = inject('tournament')
const allEntries = inject('allEntries')

const store = useMainStore()
const group = defineModel()

const loading = ref(false)
const showDialog = ref(false)
const showAddParticipantDialog = ref(false)
const selectedEntry = ref(null)

const tournamentEntries = computed(() => {
  return allEntries.value
})

const entriesArray = computed(() => {
  if (!allEntries.value) return []
  const allEntriesArray = Object.values(allEntries.value).filter(entry => entry != null)
  
  // Exclude entries already in the group (compare as strings to avoid type mismatch)
  const existingEntryIds = group.value?.group_entries?.map(ge => String(ge.entry_id)) || []
  return allEntriesArray.filter(entry => !existingEntryIds.includes(String(entry.id)))
})

const openDialog = () => {
  selectedEntry.value = null
  showDialog.value = true
}

const openAddParticipantDialog = () => {
  showAddParticipantDialog.value = true
}

const addEntryToGroup = async () => {
  if (!selectedEntry.value) {
    return
  }

  loading.value = true
  try {
    const path = store.apiPath.add_group_entry.replace('{group}', group.value.id)
    const response = await Server.post(path, { entry_id: selectedEntry.value })
    group.value.group_entries.push(response.data)

  } catch (error) {
    console.error('Error adding entry:', error)
    const message = error.response?.data?.message || 'An error occurred'
    Alerts.error(message)
  } finally {
    loading.value = false
  }

  showDialog.value = false
}

const onEntryCreated = async (entry) => {
  allEntries.value[entry.id] = entry
  selectedEntry.value = entry.id
  await addEntryToGroup()
}

</script>

<template>
  <div class="add-entry-to-group">
    <v-btn 
      color="primary" 
      variant="outlined"
      prepend-icon="mdi-plus"
      @click="openDialog"
    >
      {{ $t('button.addEntryToGroup') }}
    </v-btn>

    <v-dialog v-model="showDialog" class="common-dialog">
      <v-card>
        <v-card-title class="text-h5">
          {{ $t('label.selectParticipant') }}
        </v-card-title>

        <v-divider />

        <v-card-text>
          <v-alert
            v-if="entriesArray.length === 0"
            type="info"
            :title="$t('tournaments.noParticipantsTitle')"
            :text="$t('tournaments.noParticipantsMessage')"
            class="mb-4"
            variant="tonal"
          />

          <v-autocomplete
            v-else
            v-model="selectedEntry"
            :items="entriesArray"
            item-title="name"
            item-value="id"
            :label="$t('label.selectParticipant')"
            clearable
            auto-select-first
            :disabled="loading"
            :loading="loading"
            @update:model-value="addEntryToGroup"
          >
            <template #item="{ props, item }">
              <v-list-item
                v-bind="props"
                :prepend-avatar="item.raw.image"
                :title="item.raw.name"
                :subtitle="item.raw.description"
              />
            </template>
          </v-autocomplete>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
            color="primary"
            variant="elevated"
            prepend-icon="mdi-account-plus"
            @click="openAddParticipantDialog"
          >
            {{ $t('button.addNewParticipantToTournament') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="showDialog = false"
          >
            {{ $t('button.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog per aggiungere nuovo partecipante -->
    <EntryForm 
      v-model="showAddParticipantDialog"
      :tournamentId="tournament.id"
      @entry-created="onEntryCreated"
    />
  </div>
</template>
